import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { FaSearch, FaTimes, FaFolderOpen } from 'react-icons/fa';
import './style.css';

const CategoryDropdown = ({
  categories = [],
  selectedCategoryId,
  selectedCategoryPath,
  onCategorySelect,
  onSearch,
  loadBrowseTree,
  onBrowse,
  loading = false,
  placeholder = "Search and select a category...",
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCategories, setFilteredCategories] = useState([]);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // ---------- NEW: Inline Browse (modal ki jagah yahi pe) ----------
  const [browseMode, setBrowseMode] = useState(false);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseError, setBrowseError] = useState('');
  const [browseCurrent, setBrowseCurrent] = useState([]);
  const [browseStack, setBrowseStack] = useState([]);
  const [browsePath, setBrowsePath] = useState([]);

  const pickNodeShape = (node) => ({
    id: node?.c_id || node?._id || node?.id,
    name: node?.c_name || node?.name || node?.categoryName,
    children: node?.children || node?.subCategories || node?.subcategories || []
  });

  const onEnterNode = (node) => {
    const { children, name } = pickNodeShape(node);
    setBrowseStack((s) => [...s, browseCurrent]);
    setBrowseCurrent(children || []);
    setBrowsePath((p) => [...p, name]);
  };

  const onBack = () => {
    if (!browseStack.length) { setBrowseMode(false); return; }
    const s = [...browseStack];
    const prev = s.pop();
    setBrowseStack(s);
    setBrowseCurrent(prev || []);
    setBrowsePath((p) => p.slice(0, -1));
  };

  const onSelectNodeInline = (node) => {
    const { id, name } = pickNodeShape(node);
    const fullPath = [...browsePath, name].filter(Boolean).join(' > ');
    onCategorySelect(id, fullPath);
    setSearchTerm('');
    setFilteredCategories([]);
    setBrowseMode(false);
    setIsOpen(false);
  };
  // ---------------------------------------------------------------

  // Display label for the currently selected category
  const getSelectedCategoryName = () => {
    if (!selectedCategoryId) return placeholder;
    if (selectedCategoryPath) return selectedCategoryPath;
    const selected = categories.find(cat => cat.id === selectedCategoryId);
    return selected ? selected.path : placeholder;
  };

  // Keep only leaf categories (no further subcategories)
  const getLeafCategories = (cats) => {
    if (!Array.isArray(cats) || cats.length === 0) return [];
    const paths = cats.map(c => c.path);
    const isLeafPath = (path) => {
      const prefix = path + ' > ';
      return !paths.some(p => p !== path && p.startsWith(prefix));
    };
    return cats.filter(c => isLeafPath(c.path));
  };

  // Trigger search (manual)
  const handleSearch = () => {
    if (searchTerm.trim() && onSearch) {
      // search mode me inline-browse ko band rakhen
      setBrowseMode(false);
      setFilteredCategories([]); // clear old results (loader shows if `loading` true)
      setSearchDrill([]);        // NEW: reset search drill when firing a new search
      onSearch(searchTerm.trim());
      setIsOpen(true);
    }
  };

  // Enter key triggers search
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Update filtered list whenever the categories prop changes
  useEffect(() => {
    const leafOnly = getLeafCategories(categories);
    setFilteredCategories(leafOnly);
    // whenever new results arrive, reset the drill to the top
    setSearchDrill([]);
  }, [categories]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setBrowseMode(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when we open the result panel
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current.focus(), 50);
    }
  }, [isOpen]);

  const handleCategorySelect = (category) => {
    onCategorySelect(category.id, category.path);
    setSearchTerm('');
    setFilteredCategories([]);
    setIsOpen(false);
  };

  const clearSelection = () => {
    onCategorySelect('', '');
  };

  // Inline browse trigger
  const startInlineBrowse = useCallback(async () => {
    if (disabled) return;
    setIsOpen(true);
    setBrowseError('');
    setBrowseLoading(true);
    setBrowseMode(true);
    setBrowseStack([]);
    setBrowsePath([]);
    try {
      if (typeof loadBrowseTree === 'function') {
        const tree = await loadBrowseTree();
        setBrowseCurrent(Array.isArray(tree) ? tree : []);
      } else {
        // fallback: agar loader nahi aaya to purane modal ke liye onBrowse call kardo
        if (typeof onBrowse === 'function') onBrowse();
        // inline ko band hi rakho
        setBrowseMode(false);
      }
    } catch (e) {
      setBrowseError('Unable to load categories');
      setBrowseCurrent([]);
    } finally {
      setBrowseLoading(false);
    }
  }, [disabled, loadBrowseTree, onBrowse]);

  // ====================== NEW: GROUPED SEARCH VIEW ======================
 
  const buildTreeFromLeafPaths = useCallback((leafs) => {
    const root = { name: '__ROOT__', children: new Map(), count: 0 };
    for (const leaf of leafs) {
      const parts = (leaf.path || '').split(' > ').map(s => s.trim()).filter(Boolean);
      if (parts.length === 0) continue;
      let node = root;
      node.count += 1;
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        if (!node.children.has(part)) {
          node.children.set(part, { name: part, children: new Map(), count: 0 });
        }
        node = node.children.get(part);
        node.count += 1;

        // last part -> attach leaf reference
        if (i === parts.length - 1) {
          node.leafCategory = leaf; // { id, path, ... }
        }
      }
    }
    return root;
  }, []);

  // Drill stack for search results (array of names)
  const [searchDrill, setSearchDrill] = useState([]);

  const searchTree = useMemo(() => buildTreeFromLeafPaths(filteredCategories), [filteredCategories, buildTreeFromLeafPaths]);

  const getNodeAtPath = useCallback((tree, drillPath) => {
    let node = tree;
    for (const part of drillPath) {
      if (!node.children?.has(part)) return null;
      node = node.children.get(part);
    }
    return node;
  }, []);

  const currentSearchNode = useMemo(() => getNodeAtPath(searchTree, searchDrill) || searchTree, [searchTree, searchDrill, getNodeAtPath]);

  // Render helpers for grouped search
  const searchLevelItems = useMemo(() => {
    if (!currentSearchNode || !currentSearchNode.children) return [];
    const arr = Array.from(currentSearchNode.children.values());
    // Stable sort by name (optional), could also sort by count desc
    arr.sort((a, b) => a.name.localeCompare(b.name));
    return arr;
  }, [currentSearchNode]);

  const onSearchEnterNode = (nodeName) => {
    const node = currentSearchNode.children.get(nodeName);
    if (!node) return;
    // if it's a leaf node (has leafCategory and no children), select it immediately
    if (!node.children || node.children.size === 0) {
      const leaf = node.leafCategory;
      if (leaf) handleCategorySelect(leaf);
      return;
    }
    setSearchDrill(prev => [...prev, nodeName]);
  };

  const onSearchBack = () => {
    if (searchDrill.length === 0) {
      // close only the search panel state, not the whole dropdown
      setIsOpen(false);
      return;
    }
    setSearchDrill(prev => prev.slice(0, -1));
  };

  const onSearchSelectLeaf = (node) => {
    const leaf = node?.leafCategory;
    if (leaf) handleCategorySelect(leaf);
  };
  // =====================================================================

  // ---- UI ----
  return (
    <div className="category-dropdown" ref={dropdownRef}>
      {/* Selected Category Display */}
      {selectedCategoryId && (
        <div className="selected-category-display">
          <span className="selected-label">Selected Category:</span>
          <span className="selected-path">{getSelectedCategoryName()}</span>
          <button
            className="clear-selection-btn"
            onClick={clearSelection}
            title="Clear selection"
          >
            Change Category
          </button>
        </div>
      )}

      {/* Search Input */}
      <div className="category-search">
        <input
          ref={searchInputRef}
          type="text"
          placeholder="Enter keywords to search categories..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          className="search-input"
          disabled={disabled}
          onFocus={() => setIsOpen(true)}
        />
        <button
          className="search-button"
          onClick={handleSearch}
          disabled={!searchTerm.trim() || disabled}
          title="Search categories"
        >
          <FaSearch />
        </button>
        <span className="or-separator" aria-hidden="true">OR</span>
        <button
          type="button"
          className="browse-button"
          onClick={startInlineBrowse}
          disabled={disabled}
          title="Browse Category"
          aria-label="Browse Category"
        >
          <FaFolderOpen />
          <span className="browse-text">Browse Category</span>
        </button>
        {searchTerm && (
          <button
            className="search-clear"
            onClick={() => {
              setSearchTerm('');
              setFilteredCategories([]);
              setBrowseMode(false);
              setSearchDrill([]); // reset grouped search drill
              if (searchInputRef.current) searchInputRef.current.focus();
            }}
            title="Clear search"
          >
            <FaTimes />
          </button>
        )}
      </div>

      {/* Results Panel (always left-aligned) */}
      {(loading || browseLoading || (isOpen && (filteredCategories.length > 0 || searchTerm || browseMode))) && (
        <div
          className="search-results"
          style={{ display: 'block', paddingLeft: 0 }}
        >
          {/* SEARCH loading (only when not in browse mode) */}
          {loading && !browseMode ? (
            <div className="search-loading">
              <div className="spinner"></div>
              <span>Searching categories...</span>
            </div>
          ) : browseMode ? (
            // --------- INLINE BROWSE PANEL ----------
            <div className="browse-inline">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: '1px solid #eee' }}>
                <button
                  type="button"
                  onClick={onBack}
                  className="clear-selection-btn"
                  style={{ padding: '4px 8px' }}
                  aria-label={browseStack.length ? 'Back' : 'Close'}
                  title={browseStack.length ? 'Back' : 'Close'}
                >
                  {browseStack.length ? '←' : <FaTimes aria-hidden />}
                </button>
                <div style={{ fontWeight: 600 }}>Browse Categories</div>
                <div style={{ marginLeft: 'auto', fontSize: 12, color: '#6b7280' }}>
                  {browsePath.length ? browsePath.join(' > ') : 'All Categories'}
                </div>
              </div>

              {browseLoading ? (
                <div className="search-loading">
                  <div className="spinner"></div>
                  <span>Loading…</span>
                </div>
              ) : browseError ? (
                <div className="no-results">{browseError}</div>
              ) : browseCurrent.length === 0 ? (
                <div className="no-results">No categories here.</div>
              ) : (
                browseCurrent.map((node, idx) => {
                  const { id, name, children } = pickNodeShape(node);
                  const hasChildren = Array.isArray(children) && children.length > 0;
                  return (
                    <div
                      key={id || idx}
                      className="category-item"
                      style={{
                        paddingLeft: '0.75rem',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      <span aria-hidden style={{ width: '0.75rem', flex: '0 0 0.75rem', display: 'inline-block' }} />
                      <div
                        className="category-name"
                        title={name}
                        style={{ whiteSpace: 'normal', wordBreak: 'break-word', flex: 1 }}
                      >
                        {name}
                      </div>
                      {hasChildren ? (
                        <button
                          type="button"
                          className="browse-view-btn"
                          onClick={() => onEnterNode(node)}
                          title="View sub-categories"
                          aria-label="View sub-categories"
                        >
                          View →
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="browse-select-btn"
                          onClick={() => onSelectNodeInline(node)}
                          title="Select this category"
                          aria-label="Select this category"
                        >
                          Select
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          ) : filteredCategories.length > 0 ? (
            // --------- NEW: GROUPED SEARCH RESULTS PANEL ----------
            <div className="grouped-search">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: '1px solid #eee' }}>
                <button
                  type="button"
                  onClick={onSearchBack}
                  className="clear-selection-btn"
                  style={{ padding: '4px 8px' }}
                  aria-label={searchDrill.length ? 'Back' : 'Close'}
                  title={searchDrill.length ? 'Back' : 'Close'}
                >
                  {searchDrill.length ? '←' : <FaTimes aria-hidden />}
                </button>
                <div style={{ fontWeight: 600 }}>
                  {searchDrill.length === 0 ? 'Matched Categories' : searchDrill.join(' > ')}
                </div>
                <div style={{ marginLeft: 'auto', fontSize: 12, color: '#6b7280' }}>
                  {filteredCategories.length} result{filteredCategories.length > 1 ? 's' : ''}
                </div>
              </div>

              {searchLevelItems.length === 0 ? (
                <div className="no-results">
                  No categories found {searchTerm ? `for "${searchTerm}"` : ''}
                </div>
              ) : (
                searchLevelItems.map((node) => {
                  const isLeaf = !node.children || node.children.size === 0;
                  return (
                    <div
                      key={node.name}
                      className="category-item"
                      style={{
                        paddingLeft: '0.75rem',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      <span aria-hidden style={{ width: '0.75rem', flex: '0 0 0.75rem', display: 'inline-block' }} />
                      <div
                        className="category-name"
                        title={node.name}
                        style={{ whiteSpace: 'normal', wordBreak: 'break-word', flex: 1 }}
                      >
                        {node.name}
                      </div>
                      <div
                        className="category-count"
                        style={{ fontSize: 12, color: '#6b7280', marginRight: 8 }}
                        title={`${node.count} match${node.count > 1 ? 'es' : ''} under this`}
                      >
                        {node.count}
                      </div>
                      {isLeaf ? (
                        <button
                          type="button"
                          className="browse-select-btn"
                          onClick={() => onSearchSelectLeaf(node)}
                          title="Select this category"
                          aria-label="Select this category"
                        >
                          Select
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="browse-view-btn"
                          onClick={() => onSearchEnterNode(node.name)}
                          title="View sub-categories"
                          aria-label="View sub-categories"
                        >
                          View →
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          ) : (
            <div className="no-results">
              No categories found {searchTerm ? `for "${searchTerm}"` : ''}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CategoryDropdown;